/**
 * Animation naming convention (see README.md):
 *   texture frame name : `<state>-<dir>-<n>`      e.g. walk-left-2, idle-down-0
 *   animation key      : `<texture>:<state>-<dir>` e.g. actor-jiang-zichao:walk-left
 * Core states (contract `ActorAnimState`): idle | walk | talk | sit | write | emote. Real atlases may
 * carry extra art states (ponder, laugh, drink, sittalk, chant …): every `<state>-<dir>-<n>` frame
 * family found on a texture gets an animation, so `Actor.play('ponder')` works wherever the art has it.
 * Dirs: down | left | right | up. Frames are discovered by probing n = 0.. until a name is missing.
 * Frame rate: atlas `meta.states[state].fps` (written by the asset pipeline) → ANIM_FPS → 4.
 * A state/dir with zero frames gets no animation; Actor resolves a fallback chain instead
 * (`resolveStateChain`: requested → declared fallbacks → idle) and finally a static frame.
 */
import type Phaser from 'phaser';
import type { ActorAnimState } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import { ACTOR_DEFS } from './actorDefs';

/** Contract states plus any art-specific state name present on an atlas. */
export type ActorState = ActorAnimState | (string & {});

export const ACTOR_STATES: readonly ActorAnimState[] = ['idle', 'walk', 'talk', 'sit', 'write', 'emote'];
export const FACINGS: readonly Facing[] = ['down', 'left', 'right', 'up'];

/**
 * Default frames per second per core state, used when the atlas meta carries no fps. Walk 8 fps per
 * ART_DIRECTION; idle 3 fps = the asset pipeline's canonical 2-frame yoyo breathing (`chr-*-map.meta.json`
 * declares idle fps 3 for every actor — ART_DIRECTION's "6 fps idle" predates the art meta).
 */
export const ANIM_FPS: Record<ActorAnimState, number> = { idle: 3, walk: 8, talk: 6, sit: 2, write: 4, emote: 4 };
const DEFAULT_FPS = 4;

/**
 * Generic state fallback chain (per-actor `ActorDef.fallback` / atlas `meta.fallback` take precedence).
 * `emote` falls back to idle silently: the emote bubble is the emote, the sprite pose is optional.
 */
export const DEFAULT_STATE_FALLBACK: Readonly<Record<string, string>> = {
  walk: 'idle', talk: 'idle', sit: 'idle', write: 'sit', emote: 'idle',
  sittalk: 'sit', drink: 'sit', dim: 'sit', laugh: 'idle', ponder: 'idle',
};
const MAX_CHAIN = 8;

/** Ordered candidate states for `state`: itself, then declared fallbacks, ending with idle (cycle-safe). */
export function resolveStateChain(state: string, fallback?: Readonly<Record<string, string>>): string[] {
  const chain: string[] = [];
  let cur: string | undefined = state;
  while (cur && !chain.includes(cur) && chain.length < MAX_CHAIN) {
    chain.push(cur);
    cur = fallback?.[cur] ?? DEFAULT_STATE_FALLBACK[cur];
  }
  if (!chain.includes('idle')) chain.push('idle');
  return chain;
}

const FRAME_RE = /^([a-z][a-z0-9]*)-(down|left|right|up)-(\d+)$/;

/** State names present on a frame list (`<state>-<dir>-<n>`): core states first (contract order), then extras alphabetically. */
export function discoverStates(frameNames: Iterable<string>): string[] {
  const found = new Set<string>();
  for (const n of frameNames) { const m = FRAME_RE.exec(n); if (m) found.add(m[1] as string); }
  const core = ACTOR_STATES.filter((s) => found.has(s));
  const extra = [...found].filter((s) => !(ACTOR_STATES as readonly string[]).includes(s)).sort();
  return [...core, ...extra];
}

/** Subset of the pipeline atlas JSON `meta` block that the runtime reads (kept on `Texture.customData.meta` by Phaser's atlas parser). */
export interface AtlasMeta { states?: Record<string, { frames?: number; fps?: number }>; fallback?: Record<string, string> }

export function atlasMeta(tex: Phaser.Textures.Texture): AtlasMeta {
  const cd = (tex as { customData?: unknown }).customData;
  const meta = cd && typeof cd === 'object' ? (cd as { meta?: unknown }).meta : undefined;
  return meta && typeof meta === 'object' ? (meta as AtlasMeta) : {};
}

export function frameName(state: string, facing: Facing, n: number): string { return `${state}-${facing}-${n}`; }
export function animKey(texture: string, state: string, facing: Facing): string { return `${texture}:${state}-${facing}`; }

const registered = new Set<string>();

/** Registers global animations for a texture once per game (every state found on it). Safe to call many times. */
export function ensureActorAnims(scene: Phaser.Scene, texture: string): void {
  if (registered.has(texture)) return;
  if (!scene.textures.exists(texture)) return;
  const tex = scene.textures.get(texture);
  const def = Object.values(ACTOR_DEFS).find((d) => d.texture === texture);
  const meta = atlasMeta(tex);
  const states = new Set<string>([
    ...ACTOR_STATES,
    ...discoverStates(tex.getFrameNames()),
    ...Object.keys(def?.sheetFrames ?? {}).map((k) => k.split('-')[0] as string),
  ]);
  for (const state of states) {
    for (const facing of FACINGS) {
      const key = animKey(texture, state, facing);
      if (scene.anims.exists(key)) continue;
      const frames: Phaser.Types.Animations.AnimationFrame[] = [];
      const numeric = def?.sheetFrames?.[`${state}-${facing}`];
      if (numeric && numeric.length) {
        for (const idx of numeric) frames.push({ key: texture, frame: idx });
      } else {
        for (let n = 0; n < 64; n++) {
          const name = frameName(state, facing, n);
          if (!tex.has(name)) break;
          frames.push({ key: texture, frame: name });
        }
      }
      if (frames.length === 0) continue;
      const fps = meta.states?.[state]?.fps ?? ANIM_FPS[state as ActorAnimState] ?? DEFAULT_FPS;
      scene.anims.create({ key, frames, frameRate: fps > 0 ? fps : DEFAULT_FPS, repeat: -1, yoyo: state === 'idle' && frames.length === 2 });
    }
  }
  registered.add(texture);
}

/** Test/reset hook (animations survive scene restarts because the manager is global). */
export function forgetActorAnims(texture?: string): void { if (texture) registered.delete(texture); else registered.clear(); }
